/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Amazon Kinesis", description = "Stream events into Hightouch Events from an Amazon Kinesis data stream.", lastUpdatedDate = new Date(1791936000000), icon = "https://cdn.sanity.io/images/pwmfmi47/production/d85da86a8c7150bb8fa5612dd969752580351e69-2083x2500.svg";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Amazon Kinesis",
        description: "Stream events into Hightouch Events from an Amazon Kinesis data stream.",
        lastUpdatedDate: "Wed Oct 14 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "amazon-kinesis",
        type: "source",
        icon: "https://cdn.sanity.io/images/pwmfmi47/production/d85da86a8c7150bb8fa5612dd969752580351e69-2083x2500.svg",
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Configure your credentials",
          "depth": 1,
          "id": "configure-your-credentials"
        }, {
          "value": "Grant permissions",
          "depth": 1,
          "id": "grant-permissions"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Message format",
          "depth": 1,
          "id": "message-format"
        }, {
          "value": "Dead-letter stream",
          "depth": 1,
          "id": "dead-letter-stream"
        }, {
          "value": "Starting position",
          "depth": 1,
          "id": "starting-position"
        }, {
          "value": "Event structure",
          "depth": 0,
          "id": "event-structure"
        }, {
          "value": "Field mappings",
          "depth": 1,
          "id": "field-mappings"
        }, {
          "value": "Schema enforcement",
          "depth": 1,
          "id": "schema-enforcement"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      code: "code",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {FeatureFlag, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "The Amazon Kinesis event source"
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can read events from an Amazon Kinesis data stream in real time. After you connect a stream, Hightouch polls every open shard and converts each record into a Hightouch ", _jsxs(_components.a, {
          href: "/events/event-spec#track-events",
          children: [_jsx(_components.code, {
            children: "track"
          }), " event"]
        }), ". From there, you can load events into your ", _jsx(_components.a, {
          href: "/events/warehouses/warehouse",
          children: "warehouse"
        }), " or ", _jsx(_components.a, {
          href: "/events/event-streaming",
          children: "stream them"
        }), " to real-time destinations."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, ", _jsx(_components.a, {
          href: "https://app.hightouch.com/events/sources/new?from=docs",
          children: _jsx(_components.strong, {
            children: "create an event source"
          })
        }), " and select ", _jsx(_components.strong, {
          children: "Amazon Kinesis"
        }), " under ", _jsx(_components.strong, {
          children: "Streaming / Message queues"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-credentials",
        children: "Configure your credentials"
      }), "\n", _jsx(_components.p, {
        children: "Select an existing AWS credential or create a new one. Hightouch supports two credential types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cross-account role"
          }), " (recommended): Create an IAM role in your AWS account that trusts Hightouch, using the ", _jsx(_components.strong, {
            children: "Account ID"
          }), " and ", _jsx(_components.strong, {
            children: "External ID"
          }), " shown in Hightouch, then paste the role's ARN into Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Access key"
          }), ": Provide the ", _jsx(_components.strong, {
            children: "Access Key ID"
          }), " and ", _jsx(_components.strong, {
            children: "Secret Access Key"
          }), " of an IAM user."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For step-by-step instructions on creating either credential type, see the ", _jsx(_components.a, {
          href: "/destinations/amazon-kinesis#connect-to-your-aws-account",
          children: "Amazon Kinesis destination setup"
        }), ". The process is the same; only the required permissions differ."]
      }), "\n", _jsx(_components.h3, {
        id: "grant-permissions",
        children: "Grant permissions"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch follows the principle of least privilege. The IAM role or user needs the following IAM actions on each stream:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Stream"
            }), _jsx(_components.th, {
              align: "left",
              children: "Action"
            }), _jsx(_components.th, {
              align: "left",
              children: "Details"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Source stream"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "kinesis:DescribeStreamSummary"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Checks that the stream exists and has a status of ", _jsx(_components.code, {
                children: "ACTIVE"
              }), " or ", _jsx(_components.code, {
                children: "UPDATING"
              }), " before Hightouch starts reading."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Source stream"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "kinesis:ListShards"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Finds every open shard in the stream so Hightouch can poll each one, and discovers child shards after a split or merge."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Source stream"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "kinesis:GetShardIterator"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Gets a read position in each shard: the latest position when you first connect the stream, or the last checkpoint when Hightouch resumes after a restart."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Source stream"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "kinesis:GetRecords"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Reads records from each shard so Hightouch can convert them into events."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Dead-letter stream (optional)"
            }), _jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "kinesis:PutRecords"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Writes records that Hightouch can't process to the ", _jsx(_components.a, {
                href: "#dead-letter-stream",
                children: "dead-letter stream"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "You can use the following JSON sample to create your IAM policy. Leave out the second statement if you don't configure a dead-letter stream."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Version\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2012-10-17\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Statement\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kinesis:DescribeStreamSummary\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kinesis:ListShards\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kinesis:GetShardIterator\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kinesis:GetRecords\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:kinesis:YOUR_REGION:YOUR_ACCOUNT_ID:stream/YOUR_STREAM_NAME\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"kinesis:PutRecords\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:kinesis:YOUR_REGION:YOUR_ACCOUNT_ID:stream/YOUR_DLQ_STREAM_NAME\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Stream name"
          }), ": The name of the Kinesis data stream to read events from."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Region"
          }), ": The AWS region where the stream lives."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "DLQ stream name"
          }), " (optional): A Kinesis stream in the same region where Hightouch writes records it can't process. See ", _jsx(_components.a, {
            href: "#dead-letter-stream",
            children: "Dead-letter stream"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The stream must have a status of ", _jsx(_components.code, {
          children: "ACTIVE"
        }), " or ", _jsx(_components.code, {
          children: "UPDATING"
        }), ". Hightouch doesn't support SSH tunnels for Kinesis sources."]
      }), "\n", _jsx(_components.h3, {
        id: "message-format",
        children: "Message format"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch Events requires each Kinesis record's data to be JSON. Records that aren't valid JSON, or that exceed the 96 KB event size limit, can't be processed."
      }), "\n", _jsx(_components.h3, {
        id: "dead-letter-stream",
        children: "Dead-letter stream"
      }), "\n", _jsx(_components.p, {
        children: "If Hightouch encounters a fatal error while processing a record, such as data that isn't valid JSON, it writes the record to the dead-letter stream you configured. If you don't configure a dead-letter stream, Hightouch drops those records."
      }), "\n", _jsx(_components.p, {
        children: "Each dead-letter record uses the original record's partition key. Its data is a JSON object that contains the original record, the error, and the ID of the event source that wrote it:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"partitionKey\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sequenceNumber\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"49590338271490256608559692538361571095921575989136588898\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"shardId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"shardId-000000000000\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"streamName\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"my-stream\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"approximateArrivalTimestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2026-09-28T14:52:37.648Z\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"data\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"not valid json\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"truncated\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "false"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"error\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Unexpected token o in JSON at position 1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"hightouchEventSourceId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_EVENT_SOURCE_ID\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch truncates the original data to 2,048 characters and sets ", _jsx(_components.code, {
          children: "truncated"
        }), " to ", _jsx(_components.code, {
          children: "true"
        }), " when it's longer."]
      }), "\n", _jsx(_components.h3, {
        id: "starting-position",
        children: "Starting position"
      }), "\n", _jsx(_components.p, {
        children: "When you first connect a stream, Hightouch starts reading from the latest position in each shard. Records written to the stream before the source was created aren't ingested. After that, Hightouch checkpoints its position in each shard after every batch, so it resumes where it left off after a restart."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch also follows resharding. When a shard closes after a split or merge, Hightouch continues reading from its child shards."
      }), "\n", _jsx(_components.h2, {
        id: "event-structure",
        children: "Event structure"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch ingests every Kinesis record as a ", _jsx(_components.code, {
          children: "track"
        }), " event:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The record's partition key and parsed JSON data go into ", _jsx(_components.code, {
            children: "properties.partitionKey"
          }), " and ", _jsx(_components.code, {
            children: "properties.value"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Stream metadata (", _jsx(_components.code, {
            children: "streamName"
          }), ", ", _jsx(_components.code, {
            children: "shardId"
          }), ", and ", _jsx(_components.code, {
            children: "sequenceNumber"
          }), ") goes into ", _jsx(_components.code, {
            children: "context.kinesis"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["The record's approximate arrival time becomes the event ", _jsx(_components.code, {
            children: "timestamp"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, a record written to the ", _jsx(_components.code, {
          children: "my-stream"
        }), " stream with partition key ", _jsx(_components.code, {
          children: "user_123"
        }), " and this data:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123.45"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Is ingested as:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"My Kinesis Source Event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"partitionKey\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123.45"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"context\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"kinesis\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"streamName\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"my-stream\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"shardId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"shardId-000000000000\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sequenceNumber\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"49590338271490256608559692538361571095921575989136588898\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"timestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2026-09-28T14:52:37.648Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["By default, the event name is your event source's name followed by ", _jsx(_components.code, {
          children: "Event"
        }), ", and the message ID is built from the source ID, shard ID, and sequence number."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can configure how Hightouch maps fields in each record to Hightouch ", _jsxs(_components.a, {
          href: "/events/event-spec#track-events",
          children: [_jsx(_components.code, {
            children: "track"
          }), " events"]
        }), ". Mappings can be configured for the following fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event Name"
          }), ": a constant value or a field from the record"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "User ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Anonymous ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Message ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Timestamp"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Field paths are relative to the record's JSON data. For example, to use ", _jsx(_components.code, {
          children: "user_id"
        }), " from the record above as the user ID, enter ", _jsx(_components.code, {
          children: "user_id"
        }), ", not ", _jsx(_components.code, {
          children: "properties.value.user_id"
        }), ". If an event ends up with neither a user ID nor an anonymous ID, Hightouch generates a random anonymous ID for it."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use ", _jsx(_components.a, {
          href: "/events/functions/overview",
          children: "Functions"
        }), " to standardize schemas or enrich event data."]
      }), "\n", _jsx(_components.h3, {
        id: "schema-enforcement",
        children: "Schema enforcement"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management",
          children: "Event contracts"
        }), " can be attached to Amazon Kinesis sources just like any other event source."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
